import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Gamepad2,
  Loader2,
  Play,
  Flag,
  RotateCcw,
} from 'lucide-react';
import { gameCoverFromName } from '../../utils/gameCover';
import { isHiddenStudentGame } from '../../utils/hiddenGames';

async function fetchGameMeta(gameId, nameFromNav) {
  const id = Number(gameId);
  if (Number.isNaN(id)) return null;
  if (isHiddenStudentGame(nameFromNav)) return null;
  try {
    const res = await api.get(`/games/${id}`);
    const g = res.data?.data?.game ?? res.data?.data;
    if (g?.id != null && g?.name) {
      if (isHiddenStudentGame(g.name)) return null;
      return { id: g.id, name: g.name, status: g.status, is_deleted: g.is_deleted };
    }
  } catch {
    /* try list */
  }
  const res = await api.get(`/games?page=1&limit=200&status=active`);
  const list = res.data?.data?.games;
  if (Array.isArray(list)) {
    const g = list.find((x) => String(x.id) === String(id));
    if (g) {
      if (isHiddenStudentGame(g.name)) return null;
      return { id: g.id, name: g.name, status: g.status, is_deleted: g.is_deleted };
    }
  }
  if (nameFromNav) {
    if (isHiddenStudentGame(nameFromNav)) return null;
    return { id, name: nameFromNav, status: 'active', is_deleted: 0 };
  }
  return null;
}

const StudentGamePlay = () => {
  const { gameId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isRTL } = useLanguage();

  const nameFromNav = location.state?.name;

  const [game, setGame] = useState(null);
  const [loadingGame, setLoadingGame] = useState(true);
  const [phase, setPhase] = useState('intro');
  const [starting, setStarting] = useState(false);
  const [completing, setCompleting] = useState(false);

  const [sessionId, setSessionId] = useState(null);
  const [sessionStartMs, setSessionStartMs] = useState(null);
  const [startPayload, setStartPayload] = useState(null);

  const [completeResult, setCompleteResult] = useState(null);

  const cover = useMemo(() => gameCoverFromName(game?.name || ''), [game?.name]);

  const loadGame = useCallback(async () => {
    setLoadingGame(true);
    try {
      const meta = await fetchGameMeta(gameId, nameFromNav);
      if (!meta) {
        setGame(null);
        toast.error(t('studentGames.notFound'));
        return;
      }
      setGame(meta);
    } catch (e) {
      console.error(e);
      toast.error(t('studentGames.loadError'));
      setGame(null);
    } finally {
      setLoadingGame(false);
    }
  }, [gameId, nameFromNav, t]);

  useEffect(() => {
    loadGame();
  }, [loadGame]);

  const resetRound = useCallback(() => {
    setPhase('intro');
    setSessionId(null);
    setSessionStartMs(null);
    setStartPayload(null);
    setCompleteResult(null);
  }, []);

  const canStart =
    game &&
    game.status !== 'inactive' &&
    !game.is_deleted &&
    phase === 'intro' &&
    !starting;

  const handleStart = async () => {
    if (!game) return;
    setStarting(true);
    try {
      const res = await api.post(`/games/${game.id}/sessions`);
      if (!res.data?.success) {
        toast.error(res.data?.message || t('studentGames.startError'));
        return;
      }
      const data = res.data.data;
      const sid = data?.session?.sessionId;
      if (!sid) {
        toast.error(t('studentGames.startError'));
        return;
      }
      const startedAtIso = data?.session?.startedAt;
      const startMs = startedAtIso ? Date.parse(startedAtIso) : Date.now();
      setSessionId(sid);
      setSessionStartMs(startMs);
      setStartPayload(data);
      setPhase('playing');
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || t('studentGames.startError'));
    } finally {
      setStarting(false);
    }
  };

  const handleEndGame = async () => {
    if (!sessionId || sessionStartMs == null) return;
    const durationMs = Math.max(0, Date.now() - sessionStartMs);
    const points = 0;
    const playedAt = new Date().toISOString();
    const body = {
      result: 1,
      points,
      durationMs,
      playedAt,
    };

    setCompleting(true);
    try {
      const res = await api.post(`/games/sessions/${encodeURIComponent(sessionId)}/complete`, body);
      if (!res.data?.success) {
        toast.error(res.data?.message || t('studentGames.completeError'));
        return;
      }
      setCompleteResult({
        request: body,
        response: res.data?.data ?? res.data,
        message: res.data?.message,
      });
      setPhase('finished');
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || t('studentGames.completeError'));
    } finally {
      setCompleting(false);
    }
  };

  const backArrow = isRTL ? 'rotate-180' : '';

  if (loadingGame) {
    return (
      <div className="relative mx-auto flex min-h-[50vh] max-w-3xl flex-col items-center justify-center px-4 py-16">
        <Loader2 className="h-12 w-12 animate-spin text-violet-600" aria-hidden />
        <p className="mt-4 text-sm font-medium text-slate-600">{t('studentGames.loading')}</p>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="relative mx-auto max-w-3xl px-4 py-12 text-center">
        <p className="text-lg font-bold text-slate-800">{t('studentGames.notFound')}</p>
        <Link
          to="/student/games"
          className="mt-6 inline-flex items-center gap-2 rounded-xl border-2 border-slate-900/15 bg-white px-5 py-2.5 text-sm font-bold text-slate-800 shadow-sm hover:bg-slate-50"
        >
          <ArrowLeft className={`h-4 w-4 ${backArrow}`} aria-hidden />
          {t('studentGames.backToList')}
        </Link>
      </div>
    );
  }

  const inactive = game.status === 'inactive' || game.is_deleted;

  return (
    <div className="relative mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <div className="mb-6">
        <button
          type="button"
          onClick={() => navigate('/student/games')}
          className="inline-flex items-center gap-2 text-sm font-bold text-violet-700 hover:text-violet-900"
        >
          <ArrowLeft className={`h-4 w-4 ${backArrow}`} aria-hidden />
          {t('studentGames.backToList')}
        </button>
      </div>

      <div className="overflow-hidden rounded-3xl border-2 border-slate-900/10 bg-white shadow-[0_8px_0_0_rgba(15,23,42,0.06)]">
        <div className="relative h-40 w-full sm:h-48" style={cover.style}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.35),transparent_55%)]" />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-5xl font-black text-white/95 drop-shadow-md sm:text-6xl">{cover.label}</span>
          </div>
          <div className="absolute bottom-3 start-4 flex items-center gap-2 rounded-full bg-black/30 px-3 py-1 text-xs font-bold text-white backdrop-blur-sm">
            <Gamepad2 className="h-4 w-4" aria-hidden />
            {t('studentGames.title')}
          </div>
        </div>

        <div className="space-y-6 px-5 py-6 sm:px-8 sm:py-8">
          <div>
            <h1 className="text-2xl font-black text-slate-900 sm:text-3xl">{game.name}</h1>
            {inactive && (
              <p className="mt-2 text-sm font-semibold text-amber-800">{t('studentGames.inactiveHint')}</p>
            )}
          </div>

          {phase === 'intro' && (
            <div className="space-y-4">
              <p className="text-sm leading-relaxed text-slate-600">{t('studentGames.playIntroApi')}</p>
              <button
                type="button"
                disabled={!canStart || inactive}
                onClick={handleStart}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-slate-900 bg-gradient-to-r from-violet-600 to-fuchsia-600 px-6 py-4 text-base font-black text-white shadow-[0_4px_0_0_rgba(15,23,42,0.25)] transition hover:brightness-105 active:translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-45"
              >
                {starting ? (
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                ) : (
                  <Play className="h-5 w-5 fill-current" aria-hidden />
                )}
                {t('studentGames.start')}
              </button>
            </div>
          )}

          {phase === 'playing' && (
            <div className="space-y-6">
              <div className="rounded-2xl border-2 border-violet-200/90 bg-gradient-to-br from-violet-50 to-slate-50 px-4 py-6 text-center sm:px-6">
                <p className="text-sm font-semibold leading-relaxed text-slate-700">
                  {t('studentGames.apiGamePlaceholder')}
                </p>
              </div>
              <button
                type="button"
                disabled={completing}
                onClick={handleEndGame}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-slate-900 bg-white px-6 py-3.5 text-sm font-black text-slate-900 shadow-[0_4px_0_0_rgba(15,23,42,0.12)] transition hover:bg-slate-50 disabled:opacity-50"
              >
                {completing ? (
                  <Loader2 className="h-5 w-5 animate-spin text-violet-600" aria-hidden />
                ) : (
                  <Flag className="h-5 w-5 text-violet-600" aria-hidden />
                )}
                {t('studentGames.endGame')}
              </button>
            </div>
          )}

          {phase === 'finished' && completeResult && (
            <div className="space-y-5">
              <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/80 px-4 py-3 text-center">
                <p className="text-sm font-bold text-emerald-900">
                  {completeResult.message || t('studentGames.completeSuccess')}
                </p>
              </div>

              {startPayload?.player && (
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-xs text-slate-700">
                  <p className="font-bold text-slate-900">{t('studentGames.sessionPlayer')}</p>
                  <p className="mt-1">
                    {[startPayload.player.firstName, startPayload.player.lastName].filter(Boolean).join(' ') ||
                      startPayload.player.username}
                  </p>
                </div>
              )}

              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                  {t('studentGames.resultPayload')}
                </p>
                <pre className="max-h-64 overflow-auto rounded-xl border border-slate-200 bg-slate-900/95 p-4 text-left text-xs text-emerald-100">
                  {JSON.stringify(completeResult.response ?? completeResult.request, null, 2)}
                </pre>
              </div>

              <button
                type="button"
                onClick={resetRound}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-slate-900 bg-gradient-to-r from-cyan-500 to-violet-600 px-6 py-3.5 text-sm font-black text-white shadow-[0_4px_0_0_rgba(15,23,42,0.2)] transition hover:brightness-105"
              >
                <RotateCcw className="h-5 w-5" aria-hidden />
                {t('studentGames.playAgain')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentGamePlay;
